import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ExpenseReportDA } from '../../services/admin/expense.report.service';
import { Error } from '../../models/error.model';
import { ExpenseReport } from '../../models/expense.report.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { GlobalSwal } from '../../styles/swal.style';
import { AccountingEntry } from '../../models/accounting.entry.model';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { accountingAccountSelected } from '../../scripts/list.selected.script';
import { getError } from '../../languages/translations/response';
import { normalizeStatus } from '../../libraries/status.document.library';
import { legendInvalid, legendIconInvalid, legendValid, legendValidInvalidRestart, legendIconValidInvalidRestart, legendIconValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { uploadTooltip } from '../../utils/tooltip.util';
import { formatMoney } from '../../scripts/format.value.script';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { dateFormat, timeFormat12h } from '../../scripts/date.time.format.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { colorStatusPayment } from '../../scripts/badge.color.script';
import { calculatePercentage } from '../../scripts/financial.calculation.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';
import AppLoading from '../Loading';
import AppInputTreasuryAccount from '../element/InputTreasuryAccount';
import AppInputAccountingEntry from '../element/InputAccountingEntry';

export interface ModalPaymentCreateExpenseReportProps {
  companyForUser: Company | null,
  accountingAccountsForCompany: AccountingAccount[] | null,
  accountingDebitEntriesForCompany: AccountingEntry[] | null,
  treasuryAccountsForCompany: TreasuryAccount[] | null,
  report: ExpenseReport | null,
  setReport: Dispatch<SetStateAction<ExpenseReport | null>>,
  loadAccountingDebitEntriesForCompany: Function
}

const MODAL_ID = 'modal-payment-create-expense-report'

let errorResponse: Error;

const ModalPaymentCreateExpenseReport: React.FunctionComponent<ModalPaymentCreateExpenseReportProps> = ({companyForUser, accountingAccountsForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, report, setReport, loadAccountingDebitEntriesForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [datetimeCurrent, setDateTimeCurrent] = useState('')
  const [amountPay, setAmountPay] = useState({value: '', valid: false})
  const [amountDeposit, setAmountDeposit] = useState({value: '', valid: false})
  const [treasuryAccount, setTreasuryAccount] = useState({value: '', valid: false, object: null as any})
  const [method, setMethod] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false, starting: true})
  const [accountingEntry, setAccountingEntry] = useState({value: '', valid: false, object: null as any})

  const submitPaymentCreate = async () => {
    setLoadIndicator('on')

    let isValidTransaction: boolean = (accountingEntry.valid) ? true : (amountDeposit.valid && date.valid && treasuryAccount.valid) ? true : false

    if (companyForUser && report && amountPay.valid && isValidTransaction) {
      await ExpenseReportDA.postExpenseReportPayment(companyForUser.id, report.id, amountPay.value, amountDeposit.value, date.value, method.value, treasuryAccount.value, accountingEntry.value).then((response) => {
        if (response.status === 201 || response.status === 200) {
          const d = response.data; d.status = normalizeStatus(d.status); if (d.payment_status) d.payment_status = normalizeStatus(d.payment_status); setReport(d)
          loadAccountingDebitEntriesForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedPayment,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then(() => {
            executeHideModal()
          })
        } else {
          errorResponse = response.data
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch((error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!amountPay.value.length) legendIconInvalid(`${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-required`)
        if (!accountingEntry.value.length && !amountDeposit.value.length) legendIconInvalid(`${MODAL_ID}-input-amount-deposit`, `${MODAL_ID}-legend-amount-deposit-required`)
        if (!accountingEntry.value.length && !treasuryAccount.value.length) legendInvalid(`${MODAL_ID}-legend-treasury-account-required`)
        if (!accountingEntry.value.length && !date.value.length) legendInvalid(`${MODAL_ID}-legend-date-required`)
        if (!accountingEntry.value.length && !amountDeposit.value.length && !treasuryAccount.value.length) legendInvalid(`${MODAL_ID}-legend-accounting-entry-required`)
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeAmountPay = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setAmountPay({...amountPay, value: event.target.value})
  }

  const handleChangeAmountDeposit = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setAmountDeposit({...amountDeposit, value: event.target.value})
    setAccountingEntry({value: '', valid: false, object: null})

    if (report) {
      if (expressions.currency.test(event.target.value)) {
        if (Number(event.target.value) >= report.amount.debt) {
          setAmountPay({value: report.amount.debt.toString(), valid: true})
        } else {
          setAmountPay({value: event.target.value, valid: true})
        }
        legendIconValid(`${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-required`)
      } else {
        setAmountPay({value: '', valid: false})
        legendIconValidInvalidRestart(`${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-valid`)
      }
      legendValidInvalidRestart(`${MODAL_ID}-legend-accounting-entry-required`)
    }
  }

  const handleChangeDate = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({...date, value: event.target.value, valid: true})
      legendValid(`${MODAL_ID}-legend-date-required`)
    } else {
      setDate({...date, value: event.target.value, valid: false})
      legendInvalid(`${MODAL_ID}-legend-date-required`)
    }

    setAccountingEntry({value: '', valid: false, object: null})
    legendValidInvalidRestart(`${MODAL_ID}-legend-accounting-entry-required`)
  }

  const validateAmountPay = () => {
    evaluateLegendRequiredIcon(expressions.currency, amountPay, setAmountPay, `${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-valid`, `${MODAL_ID}-legend-amount-pay-required`)
  }

  const validateAmountDeposit = () => {
    evaluateLegendRequiredIcon(expressions.currency, amountDeposit, setAmountDeposit, `${MODAL_ID}-input-amount-deposit`, `${MODAL_ID}-legend-amount-deposit-valid`, `${MODAL_ID}-legend-amount-deposit-required`)
  }

  const executeHideModal = () => {
    modalHide(MODAL_ID)
    setTimeout(() => { restartModal() }, 200)
  }

  function datePayment(): boolean {
    switch (true) {
      case (report && date.starting && !date.valid && date.value === ''):
        setDate({value: format((new Date()), 'yyyy-MM-dd'), valid: true, starting: false})
        return true
      case (date.valid && date.value.length > 0):
        return true
      default:
        return false
    }
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  function restartModal() {
    legendIconValidInvalidRestart(`${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-valid`)
    legendIconValidInvalidRestart(`${MODAL_ID}-input-amount-pay`, `${MODAL_ID}-legend-amount-pay-required`)
    legendIconValidInvalidRestart(`${MODAL_ID}-input-amount-deposit`, `${MODAL_ID}-legend-amount-deposit-valid`)
    legendIconValidInvalidRestart(`${MODAL_ID}-input-amount-deposit`, `${MODAL_ID}-legend-amount-deposit-required`)
    legendValidInvalidRestart(`${MODAL_ID}-legend-treasury-account-required`)
    legendValidInvalidRestart(`${MODAL_ID}-legend-date-required`)
    legendValidInvalidRestart(`${MODAL_ID}-legend-accounting-entry-required`)

    setAmountPay({value: '', valid: false})
    setAmountDeposit({value: '', valid: false})
    setTreasuryAccount({value: '', valid: false, object: null})
    setMethod({value: '', valid: false})
    setDate({value: '', valid: false, starting: true})
    setAccountingEntry({value: '', valid: false, object: null})
  }

  useEffect(() => {
    uploadTooltip()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, accountingAccountsForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, report])

  useEffect(() => {
    const timerID = setInterval(() => {
      let currentDate: string = format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      setDateTimeCurrent(`${timeFormat12h(currentDate)}, ${dateFormat(lang.code, currentDate)}`)
    }, 1000)
    return () => { clearInterval(timerID) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div id={MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.reimburse}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && accountingAccountsForCompany && accountingDebitEntriesForCompany && treasuryAccountsForCompany && report
              ?
              <div className="form">
                <div className="d-flex flex-column flex-xl-row">
                  <div className="col-xl-6 mb-5 mb-xl-0">
                    <div className="h-100 m-0 pe-xl-5">
                      <div className="border border-dashed border-gray-300 card-rounded h-100 bg-lighten">
                        <h4 className="text-dark text-center text-uppercase p-3 pb-1">{lang.labels.debit}</h4>
                        <div className="border border-dashed border-gray-300 w-100"></div>
                        <div className="p-5">
                          <h6 className="text-center mb-3">{lang.labels.makeCorrespondingPayment}</h6>
                          <div className="fv-row mb-5">
                            <label className="form-label required">{lang.labels.amountToPay}</label>
                            <div className="position-relative">
                              <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder text-center w-60px h-auto px-2">{report.currency?.symbol}</div>
                              <input id={`${MODAL_ID}-input-amount-pay`} className="form-control ps-20" type="text" autoComplete="off" name="amount-pay" placeholder="0.00" value={amountPay.value} onChange={handleChangeAmountPay} onKeyUp={validateAmountPay} onBlur={validateAmountPay} />
                            </div>
                            <div className="form-text text-justify">{lang.labels.thisAmountCanNotBeGreater}</div>
                            <AppLegend component={MODAL_ID} attribute={{check: amountDeposit.valid, name: "amount-pay", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="table-responsive mb-10">
                            <table className="table align-middle table-row-bordered gy-2 mb-2">
                              <tbody>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.expenseReportNumber}</td>
                                  <td className="fw-bold text-end">{report.expense_report_number}</td>
                                </tr>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.amountTotal}</td>
                                  <td className="fw-bold text-end">{report.currency?.symbol} {formatMoney(report.amount?.total ?? 0)}</td>
                                </tr>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.amountPaid}</td>
                                  <td className="fw-bold text-end">{report.currency?.symbol} {formatMoney(report.amount?.paid ?? 0)}</td>
                                </tr>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.amountDebt}</td>
                                  <td className="fw-bold text-end">{report.currency?.symbol} {formatMoney(report.amount?.debt ?? 0)}</td>
                                </tr>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.paymentDate}</td>
                                  <td className="fw-bold text-end">{accountingEntry.valid ? dateFormat(lang.code, accountingEntry.object.date) : dateFormat(lang.code, date.value)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="d-flex flex-column align-items-center w-100">
                            <div className="d-flex justify-content-between w-100 px-1 mb-1">
                              <span className="fw-bold">{lang.labels.payment}</span>
                              <span className="fw-bolder text-secondary">{lang.labels.total}</span>
                            </div>
                            <div className={`progress bg-${colorStatusPayment(report.payment_status)} rounded h-25px w-100`}>
                              <div className="bg-success rounded text-center align-content-center fw-bolder" role="progressbar" style={{width: calculatePercentage(report.amount?.total ?? 0, report.amount?.paid ?? 0)}} aria-valuenow={((report.amount?.paid ?? 0) * 100) / (report.amount?.total || 1)} aria-valuemin={0} aria-valuemax={100}>{calculatePercentage(report.amount?.total ?? 0, report.amount?.paid ?? 0)}</div>
                            </div>
                            <div className="d-flex align-items-center justify-content-end form-text w-100">
                              <small className="fw-bold">{datetimeCurrent}</small>
                              <span className="svg-icon svg-icon-3 svg-icon-dark ms-2">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M20.9 12.9C20.3 12.9 19.9 12.5 19.9 11.9C19.9 11.3 20.3 10.9 20.9 10.9H21.8C21.3 6.2 17.6 2.4 12.9 2V2.9C12.9 3.5 12.5 3.9 11.9 3.9C11.3 3.9 10.9 3.5 10.9 2.9V2C6.19999 2.5 2.4 6.2 2 10.9H2.89999C3.49999 10.9 3.89999 11.3 3.89999 11.9C3.89999 12.5 3.49999 12.9 2.89999 12.9H2C2.5 17.6 6.19999 21.4 10.9 21.8V20.9C10.9 20.3 11.3 19.9 11.9 19.9C12.5 19.9 12.9 20.3 12.9 20.9V21.8C17.6 21.3 21.4 17.6 21.8 12.9H20.9Z" fill="black"/>
                                  <path d="M16.9 10.9H13.6C13.4 10.6 13.2 10.4 12.9 10.2V5.90002C12.9 5.30002 12.5 4.90002 11.9 4.90002C11.3 4.90002 10.9 5.30002 10.9 5.90002V10.2C10.6 10.4 10.4 10.6 10.2 10.9H9.89999C9.29999 10.9 8.89999 11.3 8.89999 11.9C8.89999 12.5 9.29999 12.9 9.89999 12.9H10.2C10.4 13.2 10.6 13.4 10.9 13.6V13.9C10.9 14.5 11.3 14.9 11.9 14.9C12.5 14.9 12.9 14.5 12.9 13.9V13.6C13.2 13.4 13.4 13.2 13.6 12.9H16.9C17.5 12.9 17.9 12.5 17.9 11.9C17.9 11.3 17.5 10.9 16.9 10.9Z" fill="black"/>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-6">
                    <div className="h-100 m-0">
                      <div className="border border-dashed border-gray-300 card-rounded h-100 bg-lighten">
                        <h4 className="text-dark text-center text-uppercase p-3 pb-1">{lang.labels.credit}</h4>
                        <div className="border border-dashed border-gray-300 w-100"></div>
                        <div className="p-5">
                          <h6 className="text-center mb-3">{lang.labels.enterNewTransaction}</h6>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                <span className="required">{lang.labels.depositAmount}</span>
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.amountMoneyWithdrawnInTransaction}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <div className="position-relative">
                              <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder text-center w-60px h-auto px-2">{report.currency?.symbol}</div>
                              <input id={`${MODAL_ID}-input-amount-deposit`} className="form-control ps-20" type="text" autoComplete="off" name="amount-deposit" placeholder="0.00" value={amountDeposit.value} onChange={handleChangeAmountDeposit} onKeyUp={validateAmountDeposit} onBlur={validateAmountDeposit} />
                            </div>
                            <AppLegend component={MODAL_ID} attribute={{check: amountDeposit.valid, name: "amount-deposit", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                          </div>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                <span className="required">{lang.labels.treasuryAccount}</span>
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.sourceTheAmountWithdrawn}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <AppInputTreasuryAccount component={MODAL_ID} solid={false} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount} setAccountingEntry={setAccountingEntry}></AppInputTreasuryAccount>
                          </div>
                          <div className="col-lg-12 fv-row">
                            <label className="form-label required">{lang.labels.paymentDate}</label>
                            <input className="form-control" type="date" name="date" max={currentDate()} value={date.value.substring(0, 10)} onChange={handleChangeDate} />
                            <AppLegend component={MODAL_ID} attribute={{check: datePayment(), name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="border border-dashed border-gray-300 w-100"></div>
                          <div className="text-center text-uppercase px-5">{lang.labels.or}</div>
                          <div className="border border-dashed border-gray-300 w-100"></div>
                        </div>
                        <div className="p-5">
                          <h6 className="text-center mb-3">{lang.labels.useExistingTransaction}</h6>
                          <div className="fv-row mb-3">
                            <label className="form-label">
                              <div className="d-flex align-items-center">
                                <span className="required">{lang.labels.transaction}</span>
                                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.transactionToRecordPayment}><FaExclamationCircle /></i>
                              </div>
                            </label>
                            <AppInputAccountingEntry component={MODAL_ID} options={{solid: false, transaction: "debit"}} companyForUser={companyForUser} accountingEntriesForCompany={accountingDebitEntriesForCompany} treasuryAccountsForCompany={treasuryAccountsForCompany} contact={report.id_contact ?? ''} record={report as any} accountingEntry={accountingEntry} setAccountingEntry={setAccountingEntry} setAmountPay={setAmountPay} setAmountDeposit={setAmountDeposit} setTreasuryAccount={setTreasuryAccount} setPaymentMethod={setMethod} setDate={setDate}></AppInputAccountingEntry>
                          </div>
                          <div className="table-responsive">
                            <table className="table align-middle table-row-bordered gy-1 mb-1">
                              <tbody>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.accountingAccount}</td>
                                  <td className="fw-bold text-end">{accountingEntry.valid ? accountingAccountSelected(accountingAccountsForCompany, accountingEntry.object.credits[0].id_account, companyForUser.language) : lang.labels.none}</td>
                                </tr>
                                <tr className="row-bordered">
                                  <td className="text-secondary">{lang.labels.totalDepositAmount}</td>
                                  <td className="fw-bold text-end">{accountingEntry.valid ? `${report.currency?.symbol} ${formatMoney(accountingEntry.object.credits[0].amount)}` : lang.labels.none}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitPaymentCreate}>
              <span className="indicator-label">{lang.labels.save}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalPaymentCreateExpenseReport;
